import { CloseCircleTwoTone, PlusOutlined } from '@ant-design/icons';
import { Alert, Checkbox, Col, Divider, Form, Input, Modal, Row, Select, Spin, theme, Tooltip, Typography } from 'antd';
import ImgCrop from 'antd-img-crop';
import Dragger from 'antd/es/upload/Dragger';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NumberInputComponent from 'src/common/components/input/number-input';
import { CommandResult } from 'src/core/stores/types';
import DatePicker from 'src/core/ui/date-components/date-picker';
import { nameof } from 'src/core/utils/object';
import { Languages } from 'src/i18n';
import { container } from 'src/inversify.config';
import {
  AnnouncementLocalizedData,
  EventType,
  ScheduledEventItem,
  ScheduledEventItemStore,
  ScheduledEventSummary,
} from 'src/stores/scheduled-events-store';

const { Text } = Typography;
const { useToken } = theme;
const { RangePicker } = DatePicker;

export type ScheduledEventDetailsProps = { onClose: (response?: CommandResult<ScheduledEventItem>) => void } & (
  | {
      mode: 'edit';
      item: ScheduledEventSummary;
    }
  | {
      mode: 'new';
    }
);
export const ScheduledEventDetails = (props: ScheduledEventDetailsProps) => {
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { mode, onClose } = props;
  const [form] = Form.useForm<ScheduledEventItem & { visibility: any }>();
  const [selectedType, setSelectedType] = useState<EventType>(mode === 'new' ? EventType.ScheduledUpdate : props.item.type);
  const localizedData: AnnouncementLocalizedData[] | null | undefined = Form.useWatch('announcementInfo', form);

  const scheduledEventStore = container.get(ScheduledEventItemStore);
  const scheduledEventState = scheduledEventStore.state;
  const { token } = useToken();
  const [submittable, setSubmittable] = useState(true);
  const values = Form.useWatch([], form);

  const defaultNewItemValues: Partial<ScheduledEventItem> = {
    type: EventType.ScheduledUpdate,
    announcementInfo: [{ language: Languages.English }],
  };

  useEffect(() => {
    form
      .validateFields()
      .then(() => {
        setSubmittable(true);
      })
      .catch((error) => {
        if (!error?.errorFields?.length && error?.outOfDate) {
          form.validateFields();
        } else setSubmittable(false);
      });
  }, [form, values]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const load = async () => {
      if (mode === 'edit') {
        setLoading(true);
        await scheduledEventStore.load(props.item.id);
        setLoading(false);
      } else {
        setLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getInitialValues = () => {
    if (!scheduledEventState.item.value) return;
    return {
      ...scheduledEventState.item.value,
      scheduledUpdateStart: scheduledEventState.item.value.scheduledUpdateStart
        ? moment(scheduledEventState.item.value.scheduledUpdateStart)
        : undefined,
    };
  };

  const onNewEvent = async () => {
    if (mode === 'edit') return;
    const item = await form.validateFields();
    const response = await scheduledEventStore.create(item);
    onClose(response);
  };

  const onEditEvent = async () => {
    if (mode === 'new') return;
    const item = await form.validateFields();
    const response = await scheduledEventStore.save(props.item.id, item);
    onClose(response);
  };

  return (
    <Modal
      maskClosable={false}
      open
      okButtonProps={{ disabled: !submittable }}
      width={selectedType === EventType.ScheduledUpdate ? 600 : 1200}
      onCancel={() => onClose()}
      title={mode === 'new' ? t('NewScheduledEvent') : t('EditScheduledEvent')}
      onOk={mode === 'new' ? onNewEvent : onEditEvent}
    >
      <Spin spinning={loading}>
        {!loading && (scheduledEventState.item.value || mode === 'new') && (
          <Form form={form} layout="vertical" initialValues={mode === 'edit' ? getInitialValues() : defaultNewItemValues}>
            <Row gutter={10}>
              <Col sm={selectedType === EventType.ScheduledUpdate ? 24 : 12} xs={24}>
                <Form.Item label={t('EventType')} name={nameof<ScheduledEventItem>('type')}>
                  <Select
                    disabled={mode === 'edit'}
                    options={Object.entries(EventType).map(([key, value]) => ({ label: t(key), value }))}
                    onChange={(v: EventType) => setSelectedType(v)}
                  />
                </Form.Item>

                <Form.Item name={nameof<ScheduledEventItem>('announcementStartDate')} hidden />
                <Form.Item name={nameof<ScheduledEventItem>('announcementEndDate')} hidden />

                <Form.Item
                  name={'visibility'}
                  label={t('Visibility')}
                  rules={[
                    () => ({
                      validator(_, value: moment.Moment[] | null) {
                        if (!value) return Promise.reject(t('PleaseEnterVisibilityRange'));
                        return Promise.resolve();
                      },
                    }),
                  ]}
                  initialValue={
                    mode === 'edit'
                      ? [moment(scheduledEventState.item.announcementStartDate.value), moment(scheduledEventState.item.announcementEndDate.value)]
                      : undefined
                  }
                >
                  <RangePicker
                    classNames={{
                      popup: {
                        root: 'responsive-range-picker',
                      },
                    }}
                    style={{ width: '100%' }}
                    onChange={(dates) => {
                      if (dates && dates[0] !== null && dates[1] !== null) {
                        const from = dates[0];
                        const to = dates[1];
                        form.setFieldsValue({ announcementStartDate: from.toISOString(), announcementEndDate: to.toISOString() });
                      }
                    }}
                  />
                </Form.Item>

                {selectedType === EventType.ScheduledUpdate && (
                  <>
                    <Row gutter={10}>
                      <Col lg={12} xs={24}>
                        <Form.Item
                          rules={[
                            () => ({
                              validator(_, value: moment.Moment | null) {
                                if (!value) return Promise.reject(t('PleaseEnterScheduledUpdateStartMoment'));
                                return Promise.resolve();
                              },
                            }),
                          ]}
                          name={nameof<ScheduledEventItem>('scheduledUpdateStart')}
                          label={t('EventStart')}
                        >
                          <DatePicker style={{ width: '100%' }} showTime />
                        </Form.Item>
                      </Col>
                      <Col lg={12} xs={24}>
                        <Form.Item
                          rules={[
                            () => ({
                              validator(_, value: moment.Moment | null) {
                                if (!value) return Promise.reject(t('PleaseEnterScheduledUpdateDuration'));
                                return Promise.resolve();
                              },
                            }),
                          ]}
                          name={nameof<ScheduledEventItem>('announcementDuration')}
                          label={t('EventDuration')}
                        >
                          <NumberInputComponent onlyIntegers />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}
                {selectedType === EventType.Announcement && (
                  <Form.Item name={nameof<ScheduledEventItem>('announcementLink')} label={t('Link')}>
                    <Input style={{ width: '100%' }} />
                  </Form.Item>
                )}
              </Col>

              {selectedType === EventType.Announcement && (
                <Col sm={12} xs={24}>
                  <Text>{t('LocalizedAnnouncementInformation')}</Text>
                  <Tooltip title={t('LocalizedAnnouncementInformationDescription')}>
                    <span style={{ marginLeft: 4 }} className="clickable-span strong">
                      {'(?)'}
                    </span>
                  </Tooltip>

                  <Form.List
                    rules={[
                      {
                        validator: (_, value) => (value && value.length > 0 ? Promise.resolve() : Promise.reject(new Error('AddAtLeastOneLanguage'))),
                      },
                    ]}
                    name={nameof<ScheduledEventItem>('announcementInfo')}
                  >
                    {(fields, { add, remove }) => (
                      <>
                        <br />
                        <Text strong>{t('Languages')}: </Text>

                        <Checkbox.Group
                          options={Object.entries(Languages).map(([key, value]) => ({ label: t(key), value }))}
                          value={localizedData ? localizedData.map((v) => v.language) : [Languages.English]}
                          onChange={(languages: Languages[]) => {
                            const indexesToRemove = localizedData?.flatMap((data, idx) => (!languages.includes(data.language) ? [idx] : []));
                            if (indexesToRemove && indexesToRemove.length > 0) remove(indexesToRemove);
                            const dataToAdd: AnnouncementLocalizedData[] = languages.flatMap((lang) =>
                              localizedData?.some((data) => data.language === lang) ? [] : [{ language: lang }]
                            );
                            for (const item of dataToAdd) {
                              add(item);
                            }
                          }}
                        />

                        <Alert type="info" style={{ marginTop: 8 }} message={t('BannerSelectInfoMessage')} />
                        <Divider />
                        {fields.length === 0 && <Alert type="error" message={t('AddAtLeastOneLanguage')} />}

                        <div style={{ maxHeight: 400, overflow: 'auto', scrollbarWidth: 'thin' }}>
                          {fields.map((field, i) => (
                            <div key={field.key} style={{ backgroundColor: token.colorBgLayout, padding: 14, borderRadius: 18, marginBottom: 10 }}>
                              {localizedData && (
                                <Text strong>{t(`${Object.entries(Languages).find((v) => v[1] === localizedData[i].language)?.[0]}`)}: </Text>
                              )}
                              <Form.Item
                                name={[field.name, 'image']}
                                label={t('Banner')}
                                dependencies={[[field.name, 'text']]}
                                rules={[
                                  () => ({
                                    validator(_, value: string) {
                                      const description = localizedData[i].text;
                                      if (!value && !description) return Promise.reject(t('PleaseLoadImageIfNoDescription'));
                                      return Promise.resolve();
                                    },
                                  }),
                                ]}
                              >
                                <ImgCrop modalTitle={t('SelectBannerArea')} aspect={8 / 1}>
                                  <Dragger
                                    accept=".jpeg"
                                    name="logo"
                                    listType="picture"
                                    showUploadList={false}
                                    beforeUpload={(file) => {
                                      const reader = new FileReader();
                                      reader.readAsDataURL(file);
                                      reader.addEventListener('load', (event) => {
                                        const loadedImageUrl = event.target && event.target.result;
                                        form.setFieldValue(['announcementInfo', i, 'image'], loadedImageUrl);
                                      });
                                      return false;
                                    }}
                                  >
                                    {localizedData && localizedData[i].image ? (
                                      <div>
                                        <div
                                          style={{ position: 'absolute', right: -10, top: -10 }}
                                          onClick={(e) => {
                                            form.setFieldValue(['announcementInfo', i, 'image'], undefined);
                                            e.preventDefault();
                                            e.stopPropagation();
                                          }}
                                        >
                                          <CloseCircleTwoTone style={{ fontSize: 24 }} twoToneColor={[token.colorBgLayout, token.colorPrimary]} />
                                        </div>
                                        <img src={localizedData[i].image} alt="logo" style={{ width: '100%', height: 'auto', maxHeight: '300px' }} />
                                      </div>
                                    ) : (
                                      <div style={{ textAlign: 'center', position: 'relative' }}>
                                        {<PlusOutlined />}
                                        <div style={{ marginTop: 12, marginBottom: 12, textAlign: 'center' }}>{t('UploadBanner')}</div>
                                      </div>
                                    )}
                                  </Dragger>
                                </ImgCrop>
                              </Form.Item>
                              <Form.Item
                                rules={[
                                  () => ({
                                    validator(_, value: string) {
                                      const image = localizedData[i].image;
                                      if (!value && !image) return Promise.reject(t('PleaseEnterDescriptionIfNoImage'));
                                      return Promise.resolve();
                                    },
                                  }),
                                ]}
                                dependencies={[[field.name, 'image']]}
                                name={[field.name, 'text']}
                                label={t('Description')}
                              >
                                <Input.TextArea style={{ width: '100%' }} />
                              </Form.Item>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </Form.List>
                </Col>
              )}
            </Row>
          </Form>
        )}
      </Spin>
    </Modal>
  );
};
